/* Homepage-only behaviour. Never handles or intercepts contact submission. */
(() => {
  if (!document.body.classList.contains('isp-home')) return;
  document.body.classList.add('isp-js');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let headerSection;
  let anchorNavigation = false;
  ['wheel', 'touchstart', 'keydown'].forEach(type => window.addEventListener(type, () => { anchorNavigation = false; }, { passive: true }));
  let previousScroll = window.scrollY;
  const mobileHeader = window.matchMedia('(max-width: 760px)');
  function measureHeader() {
    headerSection = document.querySelector('header.header')?.closest('.shopify-section');
    if (!headerSection) return;
    const strip = headerSection.querySelector('.contact-strip');
    document.documentElement.style.setProperty('--isp-strip-height', `${strip?.offsetHeight || 0}px`);
    document.documentElement.style.setProperty('--isp-header-height', `${headerSection.querySelector('header').offsetHeight}px`);
  }
  const headerObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measureHeader) : null;
  function connectHeader() {
    headerObserver?.disconnect();
    measureHeader();
    if (headerSection) headerObserver?.observe(headerSection);
  }
  window.addEventListener('scroll', () => {
    const current = Math.max(0, window.scrollY);
    if (!headerSection || Math.abs(current - previousScroll) < 5) return;
    const keepVisible = anchorNavigation || !mobileHeader.matches || current < headerSection.offsetHeight ||
      headerSection.querySelector('.nav.open') || headerSection.contains(document.activeElement);
    headerSection.classList.toggle('isp-header-hidden', !keepVisible && current > previousScroll);
    previousScroll = current;
  }, { passive: true });
  window.addEventListener('resize', measureHeader);
  document.addEventListener('focusin', () => {
    if (headerSection?.contains(document.activeElement)) headerSection.classList.remove('isp-header-hidden');
  });
  document.addEventListener('shopify:section:load', connectHeader);
  connectHeader();

  // Act only on Shopify's rendered form response, never on a query flag or a click.
  const feedback = document.querySelector('[data-isp-form-feedback]');
  if (feedback) {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    const showFeedback = () => {
      measureHeader();
      headerSection?.classList.remove('isp-header-hidden');
      feedback.focus({ preventScroll: true });
      feedback.scrollIntoView({ behavior: 'instant', block: 'start' });
    };
    // Let the native redirect/anchor and initial media layout finish first.
    const afterLayout = () => window.requestAnimationFrame(() => window.requestAnimationFrame(showFeedback));
    if (document.readyState === 'complete') afterLayout();
    else window.addEventListener('load', afterLayout, { once: true });
  }

  function initialise(root = document) {
    root.querySelectorAll('video.isp-floor-video').forEach(video => {
      if (video.dataset.ispInitialised) return;
      video.dataset.ispInitialised = 'true';
      const preference = video.parentElement.querySelector('[data-isp-video-autoplay]');
      if (preference?.dataset.ispVideoAutoplay === 'true' && !reducedMotion.matches) {
        video.muted = true;
        video.play().catch(() => {});
      }
    });
    const form = root.querySelector('#ISPHomeContact');
    if (!form || form.dataset.ispInitialised) return;
    form.dataset.ispInitialised = 'true';
    const parameters = new URLSearchParams(window.location.search);
    form.querySelectorAll('[data-isp-param]').forEach(field => {
      const value = parameters.get(field.dataset.ispParam);
      if (!value) return;
      field.value = value.slice(0, 2000);
      field.disabled = false;
    });
    const product = parameters.get('product');
    if (product) {
      form.querySelector('[data-isp-product-name]').textContent = product.slice(0, 2000);
      form.querySelector('[data-isp-context]').hidden = false;
      const interest = form.querySelector('#ISPHomeInterest');
      if (!interest.value.trim()) interest.value = product.slice(0, 2000);
    }
  }

  document.addEventListener('click', event => {
    const cookieButton = event.target.closest('.isp-cookie-preferences');
    if (cookieButton) {
      const status = cookieButton.parentElement.querySelector('.isp-cookie-status');
      const unavailable = () => {
        status.textContent = 'Cookie preferences could not be opened. Please refresh the page and try again.';
        status.hidden = false;
      };
      status.hidden = true;
      if (typeof window.privacyBanner?.showPreferences === 'function') {
        try { Promise.resolve(window.privacyBanner.showPreferences()).catch(unavailable); }
        catch { unavailable(); }
      } else unavailable();
    }
    const menu = event.target.closest('[data-isp-menu]');
    if (menu) {
      const navigation = document.getElementById(menu.getAttribute('aria-controls'));
      const open = menu.getAttribute('aria-expanded') !== 'true';
      menu.setAttribute('aria-expanded', String(open));
      navigation?.classList.toggle('open', open);
    }
    const navLink = event.target.closest('.nav a');
    if (navLink) {
      const nav = navLink.closest('.nav');
      nav.classList.remove('open');
      document.querySelector('[data-isp-menu]')?.setAttribute('aria-expanded', 'false');
      measureHeader();
    }
    const enquiry = event.target.closest('[data-isp-enquiry]');
    const enquiryLink = event.target.closest('a[href="#enquiry"]');
    if (enquiryLink) {
      anchorNavigation = true;
      headerSection?.classList.remove('isp-header-hidden');
      measureHeader();
    }
    if (enquiry && enquiry.getAttribute('href') === '#enquiry') {
      const form = document.getElementById('ISPHomeContact');
      const interest = form?.querySelector('#ISPHomeInterest');
      if (interest) {
        interest.value = enquiry.dataset.ispEnquiry;
        // A deliberate service enquiry replaces any old product query context.
        form.querySelectorAll('[data-isp-param]').forEach(field => {
          if (field.dataset.ispParam === 'source') {
            field.value = 'Homepage: ' + enquiry.dataset.ispEnquiry;
            field.disabled = false;
          } else {
            field.value = '';
            field.disabled = true;
          }
        });
        form.querySelector('[data-isp-context]').hidden = true;
        interest.focus({ preventScroll: true });
      }
    }
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    const openNav = document.querySelector('.nav.open');
    if (!openNav) return;
    openNav.classList.remove('open');
    const menu = document.querySelector('[data-isp-menu]');
    menu?.setAttribute('aria-expanded', 'false');
    menu?.focus();
  });
  reducedMotion.addEventListener('change', event => {
    if (event.matches) document.querySelectorAll('video.isp-floor-video').forEach(video => video.pause());
  });
  document.addEventListener('shopify:section:load', event => initialise(event.target));
  initialise();
})();
